// [claude-code 2026-05-05] Responsive shell compaction: icon-only header dropdown triggers, context-aware panel-toggle visibility by layout mode, and nametag suppression at marginal widths.
// [claude-code 2026-05-01] v6.0.5: removed toolbar's own border-t so it doesn't double-stroke the new MainContent top border
// [claude-code 2026-03-03] Toolbar items reorderable via getToolbarOrder/setToolbarOrder.
// [claude-code 2026-03-11] T2: IV score wired to backend /api/market-data/iv-score — replaces local quickIVScore
// [claude-code 2026-03-20] S3:T4b: Merge platform/layout into one toolbar slot; DND moves to header when iFrame active
// [claude-code 2026-03-20] S3:T4c: createPortal for platform/layout dropdowns — fixes z-index behind Strategium panel
// [claude-code 2026-03-20] S3:T5 — VIX spike toast trigger when VIX crosses above threshold
// [claude-code 2026-03-24] Change VIX risk toast from threshold-crossing to scheduled pre-market-open times (EST)
// [claude-code 2026-05-16] S67: lockout dropdown with briefing anchor options, toolbar icon colors → var(--fintheon-accent), pill bg removed, d+h timer format
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../contexts/AuthContext";
import { UpgradeModal } from "../UpgradeModal";
import { IVScoreCard } from "../IVScoreCard";
import { useBackend } from "../../lib/backend";
import { useSettings } from "../../contexts/SettingsContext";
import { useToast } from "../../contexts/ToastContext";
import {
  getToolbarOrder,
  type ToolbarItemId,
} from "../../lib/layoutOrderStorage";
import { HeaderVoiceControl } from "../voice/HeaderVoiceControl";
import { PanelToggleButton } from "./PanelToggleGroup";
import {
  Layers,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  Monitor,
  MessageCircle,
  Power,
  Bell,
  BellOff,
  ClipboardList,
  Clock705,
} from "lucide-react";
import { WhatsNewButton } from "../onboarding/WhatsNewButton";
import { StickyBulletin } from "../StickyBulletin";
import { TraderNametag } from "../TraderNametag";
import { FadingRuler } from "../shared/FadingRuler";
import { ProxVoiceHeaderControl } from "../proxvoice/ProxVoiceHeaderControl";
import type { IVScoreResponse } from "../../types/market-data";
import type { TradingPlatform } from "../TradingBrowser";
import { useDND } from "../../contexts/DNDContext";
import { useServerNotifications } from "../../contexts/NotificationsContext";
import { getNotificationDotTone } from "../../lib/notification-presentation";
import { ToolbarDnD } from "./ToolbarDnD";
import { HeaderLockButton } from "./HeaderLockButton";
import type { SurfaceCapabilities } from "../../lib/surface-capabilities";
import type { SectionId } from "../../hooks/useStickyBulletin";

type NavTab =
  | "feed"
  | "analysis"
  | "riskflow"
  | "dashboard"
  | "econ"
  | "narrative"
  | "performance"
  | "proposals"
  | "desk-ops"
  | "apparatus"
  | "settings";

const TAB_LABELS: Record<NavTab, string> = {
  dashboard: "Desk",
  feed: "Desk", // feed section removed; fallback for history
  analysis: "Consilium",
  proposals: "Desk",
  apparatus: "Apparatus",
  riskflow: "RiskFlow",
  econ: "Econ Calendar",
  narrative: "DeskMap",
  performance: "Performance",
  "desk-ops": "Desk Ops",
  settings: "Settings",
};

type LayoutOption = "tickers-only" | "combined";

const TOOLBAR_PILL_CLASS = "flex items-center gap-0.5 h-8 rounded-md px-1";

function activeIconStyle(color: string) {
  return { "--toolbar-icon-active-color": color } as React.CSSProperties;
}

interface TopHeaderProps {
  topStepXEnabled?: boolean;
  onTopStepXToggle?: () => void;
  onTopStepXDisable?: () => void;
  selectedPlatform?: TradingPlatform;
  onPlatformSelect?: (platform: TradingPlatform) => void;
  layoutOption?: LayoutOption;
  onLayoutOptionChange?: (option: LayoutOption) => void;
  chatOpen?: boolean;
  onChatToggle?: () => void;
  activeTab?: NavTab;
  tabHistory?: NavTab[];
  historyIndex?: number;
  onBack?: () => void;
  onForward?: () => void;
  hideBranding?: boolean;
  psychAssistHeadingWidget?: React.ReactNode;
  voiceRoomWidget?: React.ReactNode;
  performanceChatWidget?: React.ReactNode;
  econCountdownWidget?: React.ReactNode;
  toolbarEditMode?: boolean;
  compactLevel?: 0 | 1 | 2;
  allowCustomIframes?: boolean;
  surfaceCapabilities?: SurfaceCapabilities;
  mobileDrawerOpen?: boolean;
  onMobileDrawerToggle?: () => void;
}

export function TopHeader({
  topStepXEnabled = false,
  onTopStepXToggle,
  onTopStepXDisable,
  selectedPlatform = "topstepx",
  onPlatformSelect,
  layoutOption = "combined",
  onLayoutOptionChange,
  chatOpen = false,
  onChatToggle,
  activeTab = "dashboard",
  tabHistory = [],
  historyIndex = 0,
  onBack,
  onForward,
  psychAssistHeadingWidget,
  voiceRoomWidget,
  performanceChatWidget,
  econCountdownWidget,
  toolbarEditMode = false,
  compactLevel = 0,
  allowCustomIframes = true,
  surfaceCapabilities,
  mobileDrawerOpen = false,
  onMobileDrawerToggle,
}: TopHeaderProps) {
  const { tier } = useAuth();
  const backend = useBackend();
  const { selectedSymbol, traderName, alertConfig, proposerIframeSources } =
    useSettings();
  const { addToast } = useToast();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [ivData, setIvData] = useState<IVScoreResponse | null>(null);
  const [ivLoading, setIvLoading] = useState(true);
  const [showLayoutDropdown, setShowLayoutDropdown] = useState(false);
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [toolbarOrder, setToolbarOrderState] = useState<ToolbarItemId[]>(() =>
    getToolbarOrder(),
  );
  const [showBulletin, setShowBulletin] = useState(false);
  const [bulletinInitialSection, setBulletinInitialSection] = useState<
    SectionId | undefined
  >();
  const bulletinBtnRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const platformDropdownRef = useRef<HTMLDivElement>(null);
  const layoutPortalRef = useRef<HTMLDivElement>(null);
  const platformPortalRef = useRef<HTMLDivElement>(null);
  const [layoutDropdownPos, setLayoutDropdownPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [platformDropdownPos, setPlatformDropdownPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const { dndActive, toggleManualDnd, queueCount, queue } = useDND();
  // [claude-code 2026-04-25] S35-Unified: badge counts server-side notifications + local queue.
  const { unreadCount: serverUnread, notifications: serverNotifications } =
    useServerNotifications();
  // [claude-code 2026-04-29] S53-T3: Econ watch health moved to FooterToolbar (S55)
  const totalBadgeCount = queueCount + serverUnread;
  const bulletinDotTone = getNotificationDotTone(serverNotifications, queue);
  const [quickClockPulse, setQuickClockPulse] = useState(false);
  const showIframeControls = allowCustomIframes;
  const isMobileSurface =
    surfaceCapabilities?.navigationMode === "underlay-drawer";
  const showMobileBack = isMobileSurface && historyIndex > 0;
  const allowVoiceAssistant = surfaceCapabilities?.allowVoiceAssistant ?? true;
  const shouldShowLeftPanelToggle =
    !isMobileSurface && !topStepXEnabled && compactLevel < 2;
  const shouldShowRightPanelToggle =
    !(topStepXEnabled && layoutOption === "tickers-only") && !isMobileSurface;
  const handleQuickClock = useCallback(async () => {
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    const dayOfWeek = now.getDay();
    setQuickClockPulse(true);
    setTimeout(() => setQuickClockPulse(false), 600);
    try {
      const apiBase = (
        import.meta.env.VITE_API_URL || "http://localhost:8080"
      ).replace(/\/$/, "");
      await fetch(`${apiBase}/api/sticky-bulletin/antilag`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ time, dayOfWeek, instrument: "ES", notes: "" }),
      });
    } catch {}
  }, []);
  useEffect(() => {
    setToolbarOrderState(getToolbarOrder());
  }, []);

  useEffect(() => {
    const handleOpenBulletinSection = (event: Event) => {
      const section = (event as CustomEvent<{ section?: SectionId }>).detail
        ?.section;
      setBulletinInitialSection(section ?? "inbox");
      setShowBulletin(true);
    };
    const handleCloseBulletin = () => setShowBulletin(false);
    window.addEventListener(
      "fintheon:open-bulletin-section",
      handleOpenBulletinSection,
    );
    window.addEventListener("fintheon:close-bulletin", handleCloseBulletin);
    return () => {
      window.removeEventListener(
        "fintheon:open-bulletin-section",
        handleOpenBulletinSection,
      );
      window.removeEventListener(
        "fintheon:close-bulletin",
        handleCloseBulletin,
      );
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (showLayoutDropdown) {
        const inTrigger = dropdownRef.current?.contains(target);
        const inPortal = layoutPortalRef.current?.contains(target);
        if (!inTrigger && !inPortal) setShowLayoutDropdown(false);
      }
      if (showPlatformDropdown) {
        const inTrigger = platformDropdownRef.current?.contains(target);
        const inPortal = platformPortalRef.current?.contains(target);
        if (!inTrigger && !inPortal) setShowPlatformDropdown(false);
      }
    };

    if (showLayoutDropdown || showPlatformDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showLayoutDropdown, showPlatformDropdown]);

  // Calculate fixed position for portaled dropdowns
  useEffect(() => {
    if (!showLayoutDropdown || !dropdownRef.current) {
      setLayoutDropdownPos(null);
      return;
    }
    const rect = dropdownRef.current.getBoundingClientRect();
    const dropdownW = 288; // w-72 = 18rem
    let left = rect.right - dropdownW;
    if (left < 16) left = 16;
    setLayoutDropdownPos({ top: rect.bottom + 8, left });
  }, [showLayoutDropdown]);

  useEffect(() => {
    if (!showPlatformDropdown || !platformDropdownRef.current) {
      setPlatformDropdownPos(null);
      return;
    }
    const rect = platformDropdownRef.current.getBoundingClientRect();
    const dropdownW = 288;
    let left = rect.right - dropdownW;
    if (left < 16) left = 16;
    setPlatformDropdownPos({ top: rect.bottom + 8, left });
  }, [showPlatformDropdown]);

  // [claude-code 2026-04-24] Header dropdown is fully driven by the user-managed
  // iFrame catalogue (Settings → iFrames). No hardcoded list — adding/removing
  // entries there is what changes what shows up here.
  const platformOptions: Array<{
    value: TradingPlatform;
    label: string;
    description: string;
  }> = proposerIframeSources.map((s) => ({
    value: s.id as TradingPlatform,
    label: s.label,
    description: s.url,
  }));

  const selectedPlatformLabel =
    platformOptions.find((opt) => opt.value === selectedPlatform)?.label ??
    platformOptions[0]?.label ??
    "—";

  const layoutOptions: Array<{
    value: LayoutOption;
    label: string;
    description: string;
    icon: React.ReactNode;
  }> = [
    {
      value: "combined",
      label: "Castra",
      description: "Mission Control and RiskFlow stacked on the right",
      icon: <Layers className="w-4 h-4" />,
    },
    {
      value: "tickers-only",
      label: "Zen",
      description: "Supports split-frame browser view",
      icon: <Monitor className="w-4 h-4" />,
    },
  ];

  // Fetch blended IV score from backend — SSE first, 60s polling fallback
  useEffect(() => {
    let es: EventSource | null = null;
    let fallbackInterval: ReturnType<typeof setInterval> | null = null;
    const apiBase = (
      import.meta.env.VITE_API_URL || "http://localhost:8080"
    ).replace(/\/$/, "");

    const fetchIVScore = async () => {
      try {
        const data = await backend.marketData.getIVScore(selectedSymbol.symbol);
        setIvData(data);
      } catch (error) {
        console.warn("[IV] Failed to fetch IV score:", error);
      } finally {
        setIvLoading(false);
      }
    };

    const startFallbackPolling = () => {
      if (fallbackInterval) return;
      fallbackInterval = setInterval(fetchIVScore, 60_000);
    };

    fetchIVScore();

    try {
      es = new EventSource(
        `${apiBase}/api/market-data/iv-score/stream?symbol=${encodeURIComponent(selectedSymbol.symbol)}`,
      );

      es.addEventListener("iv-score", (event) => {
        try {
          const data = JSON.parse(
            (event as MessageEvent).data,
          ) as IVScoreResponse;
          setIvData(data);
          setIvLoading(false);
        } catch {
          // Ignore malformed stream payloads
        }
      });

      es.onerror = () => {
        es?.close();
        es = null;
        startFallbackPolling();
      };
    } catch {
      startFallbackPolling();
    }

    return () => {
      es?.close();
      if (fallbackInterval) clearInterval(fallbackInterval);
    };
  }, [backend, selectedSymbol.symbol]);

  // VIX risk toast — fires ~5-10 min before each market session open (EST), only when VIX is elevated
  // Sessions: 9:30 AM, 10:00 AM(?), 11:30 AM(?), 12:25 PM(?), 6:00 PM Sun futures open
  const firedWindowsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!ivData) return;
    const threshold = alertConfig.vixSpikeThreshold ?? 22;
    const vixLevel = ivData.vix.level;

    const checkSchedule = () => {
      if (vixLevel < threshold) return;

      const now = new Date();
      // Convert to EST (UTC-5) / EDT (UTC-4)
      const estStr = now.toLocaleString("en-US", {
        timeZone: "America/New_York",
      });
      const est = new Date(estStr);
      const h = est.getHours();
      const m = est.getMinutes();
      const day = est.getDay(); // 0=Sun

      // Pre-market-open alert windows (EST): 9:20, 9:50, 11:20, 12:15, 5:50 (Sun only)
      const windows: Array<{
        h: number;
        m: number;
        sunOnly?: boolean;
        label: string;
      }> = [
        { h: 9, m: 20, label: "9:30 AM open" },
        { h: 9, m: 50, label: "10:00 AM session" },
        { h: 11, m: 20, label: "11:30 AM session" },
        { h: 12, m: 15, label: "12:25 PM session" },
        { h: 17, m: 50, sunOnly: true, label: "6:00 PM futures open" },
      ];

      const dateKey = `${est.getFullYear()}-${est.getMonth()}-${est.getDate()}`;

      for (const w of windows) {
        if (w.sunOnly && day !== 0) continue;
        // Fire within a 3-minute window (e.g. 9:20-9:22)
        if (h === w.h && m >= w.m && m <= w.m + 2) {
          const key = `${dateKey}-${w.h}:${w.m}`;
          if (!firedWindowsRef.current.has(key)) {
            firedWindowsRef.current.add(key);
            addToast(
              `VIX at ${vixLevel.toFixed(1)} — consider reducing risk`,
              "vix",
              `Elevated ahead of ${w.label}`,
              "vix-spike",
              "top-right",
            );
            break; // one toast per check
          }
        }
      }

      // Clean stale keys from previous days
      for (const key of firedWindowsRef.current) {
        if (!key.startsWith(dateKey)) firedWindowsRef.current.delete(key);
      }
    };

    checkSchedule();
    const interval = setInterval(checkSchedule, 30_000); // check every 30s
    return () => clearInterval(interval);
  }, [ivData, alertConfig.vixSpikeThreshold, addToast]);

  const getTierDisplayName = () => {
    switch (tier) {
      case "free":
        return "Pleb";
      case "fintheon":
        return "Equestrian";
      case "fintheon_plus":
        return "Equestrian+";
      case "fintheon_pro":
        return "Consul";
      default:
        return "Pleb";
    }
  };

  const headerJustifyClass = isMobileSurface
    ? "justify-center px-3"
    : "justify-between px-3";
  const leftClusterClass = isMobileSurface
    ? "absolute left-3 top-1/2 z-20 -translate-y-1/2"
    : "";
  const rightClusterClass = isMobileSurface
    ? "pointer-events-none absolute inset-0 z-10"
    : "";
  const rightInnerClass = isMobileSurface
    ? "contents"
    : "flex items-center gap-2 flex-shrink-0";
  const mobileTickerWrapperClass = isMobileSurface
    ? "pointer-events-auto absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 justify-center"
    : "";

  return (
    <div
      id="fintheon-heading-toolbar"
      data-tour-target="toolbar"
      className={`fintheon-side-surface relative bg-[var(--fintheon-surface)] flex items-center ${headerJustifyClass} ${topStepXEnabled && layoutOption === "tickers-only" ? "h-[47px]" : "h-[50px]"}`}
    >
      <div
        className={`flex items-center gap-2 lg:gap-4 xl:gap-6 ${leftClusterClass}`}
      >
        <div className="flex items-center gap-3">
          {isMobileSurface && (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={showMobileBack ? onBack : onMobileDrawerToggle}
                aria-label={
                  showMobileBack
                    ? "Back"
                    : mobileDrawerOpen
                      ? "Close mobile menu"
                      : "Open mobile menu"
                }
                aria-expanded={showMobileBack ? undefined : mobileDrawerOpen}
                className={`relative grid h-9 w-9 place-items-center overflow-hidden rounded-full border transition-[border-color,background,color,transform] active:scale-95 ${
                  mobileDrawerOpen && !showMobileBack
                    ? "border-[var(--fintheon-accent)]/45 bg-[var(--fintheon-accent)] text-black"
                    : "border-[var(--fintheon-accent)]/28 bg-[var(--fintheon-bg)] text-[var(--fintheon-accent)]"
                }`}
                title={
                  showMobileBack
                    ? "Back"
                    : mobileDrawerOpen
                      ? "Close menu"
                      : "Open menu"
                }
              >
                <ChevronLeft
                  className={`absolute h-4 w-4 transition-[opacity,transform,filter] duration-200 ${
                    showMobileBack
                      ? "translate-x-0 scale-100 opacity-100 blur-0"
                      : "-translate-x-2 scale-90 opacity-0 blur-[1px]"
                  }`}
                />
                <Menu
                  className={`absolute h-4 w-4 transition-[opacity,transform,filter] duration-200 ${
                    showMobileBack
                      ? "translate-x-2 scale-90 opacity-0 blur-[1px]"
                      : "translate-x-0 scale-100 opacity-100 blur-0"
                  }`}
                />
              </button>
            </div>
          )}
          {shouldShowLeftPanelToggle && (
            <PanelToggleButton side="left" label="left panel" />
          )}

          {/* Breadcrumb navigation — back/forward + section name */}
          {!isMobileSurface && !topStepXEnabled && compactLevel < 2 && (
            <div className="flex items-center gap-1">
              <button
                onClick={onBack}
                disabled={historyIndex <= 0}
                className="p-1 rounded text-gray-500 hover:text-[var(--fintheon-accent)] disabled:text-gray-700 disabled:cursor-default transition-colors"
                title="Back"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onForward}
                disabled={historyIndex >= tabHistory.length - 1}
                className="p-1 rounded text-gray-500 hover:text-[var(--fintheon-accent)] disabled:text-gray-700 disabled:cursor-default transition-colors"
                title="Forward"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              {compactLevel < 1 && (
                <span className="text-[10px] tracking-[0.18em] uppercase text-gray-300 ml-2">
                  {TAB_LABELS[activeTab] || activeTab}
                </span>
              )}
            </div>
          )}

          {compactLevel < 1 && (
            <div className="hidden xl:flex items-center h-7 overflow-hidden rounded-md border border-[var(--fintheon-accent)]/20 bg-[var(--fintheon-bg)]">
              {traderName && (
                <>
                  <TraderNametag
                    name={traderName}
                    variant="embedded"
                    disablePulse={!(alertConfig.nametagEmoPulse ?? true)}
                  />
                  <FadingRuler orientation="vertical" className="mx-0.5" />
                </>
              )}
              <button
                onClick={() => setShowUpgrade(true)}
                className="h-full px-2.5 text-[11px] text-gray-300 hover:text-[var(--fintheon-accent)] transition-colors"
              >
                <span className="whitespace-nowrap">
                  {getTierDisplayName()}
                </span>
              </button>
            </div>
          )}
          {(compactLevel < 2 || topStepXEnabled) && (
            <div className={TOOLBAR_PILL_CLASS}>
              {allowVoiceAssistant && compactLevel < 1 && (
                <ProxVoiceHeaderControl />
              )}
              {compactLevel < 1 && topStepXEnabled && (
                <FadingRuler orientation="vertical" className="mx-0.5" />
              )}
              {topStepXEnabled && (
                <button
                  onClick={() => {
                    if (totalBadgeCount > 0) {
                      setBulletinInitialSection("inbox");
                      setShowBulletin(true);
                      return;
                    }
                    toggleManualDnd();
                  }}
                  className={`relative toolbar-icon-btn ${dndActive ? "toolbar-active" : ""}`}
                  title={
                    totalBadgeCount > 0
                      ? "Open notification inbox"
                      : dndActive
                        ? "Do Not Disturb (ON)"
                        : "Notifications"
                  }
                >
                  {dndActive ? (
                    <BellOff className="w-3 h-3 toolbar-icon-active" />
                  ) : (
                    <Bell className="w-3 h-3" />
                  )}
                  {totalBadgeCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-500/80 text-white text-[8px] font-bold leading-none">
                      {totalBadgeCount > 99 ? "99+" : totalBadgeCount}
                    </span>
                  )}
                </button>
              )}
              {((compactLevel < 1 && topStepXEnabled) ||
                (compactLevel < 2 && topStepXEnabled)) &&
                compactLevel < 2 && (
                  <FadingRuler orientation="vertical" className="mx-0.5" />
                )}
              {compactLevel < 2 && (
                <button
                  onClick={handleQuickClock}
                  className={`toolbar-icon-btn ${quickClockPulse ? "toolbar-active" : ""}`}
                  title="Quick clock antilag"
                >
                  <Clock705
                    className={`w-3 h-3 ${quickClockPulse ? "toolbar-icon-active" : ""}`}
                  />
                </button>
              )}
              {compactLevel < 2 && (
                <FadingRuler orientation="vertical" className="mx-0.5" />
              )}
              {compactLevel < 2 && <HeaderLockButton />}
            </div>
          )}
          {compactLevel < 2 && voiceRoomWidget}
        </div>
      </div>

      <div
        className={`flex items-center gap-1.5 lg:gap-3 min-w-0 shrink-0 ${rightClusterClass}`}
      >
        <div className={rightInnerClass}>
          {compactLevel < 1 && <WhatsNewButton />}
          {psychAssistHeadingWidget}
          {econCountdownWidget}
          {activeTab === "performance" && performanceChatWidget}
          {showIframeControls &&
            (topStepXEnabled && onLayoutOptionChange ? (
              // iFrame active → Castra/Zen layout dropdown
              <div className="relative" ref={dropdownRef}>
                {/* [claude-code 2026-04-27] S46.4/F: chip chrome stripped per TP —
                  trigger sits flush with the toolbar like other icon buttons.
                  Resting state: no bg + no border. Hover keeps the accent flash. */}
                <button
                  onClick={() => setShowLayoutDropdown(!showLayoutDropdown)}
                  className="px-2.5 h-7 rounded-lg text-xs font-medium text-[var(--fintheon-accent)] hover:bg-[var(--fintheon-accent)]/10 hover:border hover:border-[var(--fintheon-accent)]/40 transition-colors flex items-center gap-1.5"
                  aria-label="Layout Options"
                  title="Layout Options"
                >
                  {
                    layoutOptions.find((opt) => opt.value === layoutOption)
                      ?.icon
                  }
                  {compactLevel < 1 && (
                    <span className="fintheon-zen-label">
                      {
                        layoutOptions.find((opt) => opt.value === layoutOption)
                          ?.label
                      }
                    </span>
                  )}
                  <ChevronDown
                    className={`w-3 h-3 transition-transform ${showLayoutDropdown ? "rotate-180" : ""}`}
                  />
                </button>
                {showLayoutDropdown &&
                  layoutDropdownPos &&
                  createPortal(
                    <div
                      ref={layoutPortalRef}
                      style={{
                        position: "fixed",
                        top: layoutDropdownPos.top,
                        left: layoutDropdownPos.left,
                        zIndex: 9999,
                      }}
                      className="w-72 bg-[var(--fintheon-surface)] border border-[var(--fintheon-accent)]/20 rounded-lg shadow-xl overflow-hidden animate-dropdown-enter"
                    >
                      {layoutOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            onLayoutOptionChange(option.value);
                            setShowLayoutDropdown(false);
                          }}
                          className={`w-full border border-transparent px-4 py-3 text-left hover:bg-[var(--fintheon-accent)]/10 transition-colors flex items-start gap-3 ${
                            layoutOption === option.value
                              ? "border-[var(--fintheon-accent)]/28 text-[var(--fintheon-accent)]"
                              : ""
                          }`}
                        >
                          <div className="mt-0.5 text-[var(--fintheon-accent)]">
                            {option.icon}
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-[var(--fintheon-accent)] mb-1">
                              {option.label}
                            </div>
                            <div className="text-xs text-gray-400">
                              {option.description}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>,
                    document.body,
                  )}
              </div>
            ) : (
              // iFrame off → platform/browser selection dropdown
              <div className="relative" ref={platformDropdownRef}>
                {/* [claude-code 2026-04-27] S46.4/F: chip chrome stripped per TP. */}
                <button
                  onClick={() => setShowPlatformDropdown(!showPlatformDropdown)}
                  className="px-2.5 h-7 rounded-lg text-xs font-medium text-[var(--fintheon-accent)] hover:bg-[var(--fintheon-accent)]/10 hover:border hover:border-[var(--fintheon-accent)]/40 transition-colors flex items-center gap-1.5"
                  aria-label="Select trading platform"
                  title="Select trading platform"
                >
                  <Monitor className="w-3 h-3" />
                  {compactLevel < 1 && (
                    <span className="fintheon-zen-label">
                      {selectedPlatformLabel}
                    </span>
                  )}
                  <ChevronDown
                    className={`w-3 h-3 transition-transform ${showPlatformDropdown ? "rotate-180" : ""}`}
                  />
                </button>
                {showPlatformDropdown &&
                  platformDropdownPos &&
                  createPortal(
                    <div
                      ref={platformPortalRef}
                      style={{
                        position: "fixed",
                        top: platformDropdownPos.top,
                        left: platformDropdownPos.left,
                        zIndex: 9999,
                      }}
                      className="w-72 bg-[var(--fintheon-surface)] border border-[var(--fintheon-accent)]/20 rounded-lg shadow-xl overflow-hidden py-1 animate-dropdown-enter"
                    >
                      {platformOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            onPlatformSelect?.(option.value);
                            setShowPlatformDropdown(false);
                          }}
                          className={`w-full px-4 py-3 text-left transition-colors ${
                            selectedPlatform === option.value
                              ? "bg-[var(--fintheon-accent)]/15"
                              : "hover:bg-[var(--fintheon-accent)]/8"
                          }`}
                        >
                          <div
                            className={`text-xs font-semibold tracking-[0.14em] uppercase ${
                              selectedPlatform === option.value
                                ? "text-[var(--fintheon-accent)]"
                                : "text-gray-200"
                            }`}
                          >
                            {option.label}
                          </div>
                          <div
                            className={`text-[10px] mt-0.5 ${
                              selectedPlatform === option.value
                                ? "text-[var(--fintheon-accent)]/60"
                                : "text-gray-500"
                            }`}
                          >
                            {option.description}
                          </div>
                        </button>
                      ))}
                    </div>,
                    document.body,
                  )}
              </div>
            ))}
          {!isMobileSurface && (
            <div className="bg-[var(--fintheon-bg)] border border-zinc-800 rounded-lg px-2.5 h-7 flex items-center flex-shrink-0">
              <div className="flex items-center gap-1.5">
                {compactLevel < 2 && (
                  <span className="text-[9px] text-gray-500">VIX</span>
                )}
                <span className="text-xs font-mono text-gray-300">
                  {ivData ? ivData.vix.level.toFixed(2) : "--"}
                </span>
              </div>
            </div>
          )}
          <div className={mobileTickerWrapperClass}>
            {isMobileSurface ? (
              <IVScoreCard
                data={ivData}
                loading={ivLoading}
                layoutOption={layoutOption}
                compactCopy={!topStepXEnabled && compactLevel >= 1}
                mobileCombined
              />
            ) : (
              <ToolbarDnD
                items={toolbarOrder}
                editMode={toolbarEditMode}
                onOrderChange={setToolbarOrderState}
              >
                {(id) => {
                  if (id === "ivScore") {
                    return (
                      <IVScoreCard
                        data={ivData}
                        loading={ivLoading}
                        layoutOption={layoutOption}
                        compactCopy={!topStepXEnabled && compactLevel >= 1}
                      />
                    );
                  }
                  return null;
                }}
              </ToolbarDnD>
            )}
          </div>
          <div
            className={`${TOOLBAR_PILL_CLASS} ${
              isMobileSurface
                ? "pointer-events-auto absolute right-2 top-1/2 -translate-y-1/2 mr-1.5"
                : ""
            }`}
          >
            {showIframeControls && onTopStepXDisable && (
              <button
                onClick={onTopStepXDisable}
                className={`toolbar-icon-btn ${topStepXEnabled ? "toolbar-active" : ""}`}
                title={
                  topStepXEnabled
                    ? "Power off trading browser"
                    : "Power on trading browser"
                }
                aria-label={
                  topStepXEnabled
                    ? "Power off trading browser"
                    : "Power on trading browser"
                }
              >
                <Power
                  className={`w-3 h-3 ${topStepXEnabled ? "toolbar-icon-active" : ""}`}
                  style={
                    topStepXEnabled ? activeIconStyle("#34d399") : undefined
                  }
                />
              </button>
            )}
            {showIframeControls && onTopStepXDisable && (
              <FadingRuler orientation="vertical" className="mx-0.5" />
            )}
            <button
              ref={bulletinBtnRef}
              onClick={() => {
                setBulletinInitialSection(undefined);
                setShowBulletin(!showBulletin);
              }}
              className={`relative toolbar-icon-btn ${showBulletin ? "toolbar-active" : ""}`}
              title="Bulletin"
            >
              <ClipboardList
                className={`w-3 h-3 ${showBulletin ? "toolbar-icon-active" : ""}`}
              />
              {bulletinDotTone !== "none" && (
                <span
                  className={`absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-[var(--fintheon-bg)] ${
                    bulletinDotTone === "error"
                      ? "bg-red-500"
                      : "bg-[var(--fintheon-accent)]"
                  }`}
                />
              )}
            </button>
            <StickyBulletin
              open={showBulletin}
              onClose={() => setShowBulletin(false)}
              anchorRef={bulletinBtnRef}
              variant={isMobileSurface ? "mobile-dropdown" : "desktop-popover"}
              initialSection={bulletinInitialSection}
            />
            {(!isMobileSurface || allowVoiceAssistant) && (
              <FadingRuler orientation="vertical" className="mx-0.5" />
            )}
            {!isMobileSurface && onChatToggle && (
              <button
                onClick={onChatToggle}
                className={`toolbar-icon-btn ${chatOpen ? "toolbar-active" : ""}`}
                title="Convene"
              >
                <MessageCircle
                  className={`w-3 h-3 ${chatOpen ? "toolbar-icon-active" : ""}`}
                />
              </button>
            )}
            {!isMobileSurface && onChatToggle && (
              <FadingRuler orientation="vertical" className="mx-0.5" />
            )}
            {allowVoiceAssistant && (
              <HeaderVoiceControl
                compact={topStepXEnabled && layoutOption === "tickers-only"}
              />
            )}
          </div>
          {shouldShowRightPanelToggle && (
            <PanelToggleButton side="right" label="right panel" />
          )}
        </div>
      </div>
      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
    </div>
  );
}
